/* eslint-disable @typescript-eslint/no-unused-expressions */
import { FormikProps } from "formik";
import { useEffect, useMemo, useRef } from "react";
import { Form } from "react-bootstrap";
import Select, {
  components as RSComponents,
  GroupBase,
  MenuListProps,
  OptionsOrGroups,
  Props,
} from "react-select";

interface IProps extends Props {
  name: string;
  label?: string;
  options: OptionsOrGroups<
    { label: any; value: any },
    GroupBase<{ label: any; value: any }>
  >;
  formik?: FormikProps<any>;
  formikErrorMesg?: any;
  margin?: boolean;
  isClearable?: boolean;
  /** Called as the menu nears the bottom, to prefetch the next page. */
  onLoadMore?: () => void;
  /** Shows a "Loading…" footer while the next page is being fetched. */
  isLoadingMore?: boolean;
}

/** Trigger prefetch this many pixels before the actual bottom for smoothness. */
const LOAD_MORE_THRESHOLD_PX = 150;

const ReactSelectField: React.FC<IProps> = ({
  name,
  formik,
  options,
  label,
  formikErrorMesg,
  value,
  margin,
  isClearable = false,
  onLoadMore,
  isLoadingMore,
  ...rest
}) => {
  const formikValue = formik?.values[name] ? formik?.values[name] : value;
  const formikError = formik?.touched[name] ? formik?.errors[name] : null;
  useEffect(() => {
    if (name === "country_id" && !formikValue?.value) {
      formik?.setFieldValue("country_id", { value: 1, label: "United States" });
    }
  }, [formikValue]);

  // Read the latest callbacks/flags through refs so the custom MenuList keeps a
  // stable identity (recreating it on every load would reset the scroll position).
  const loadMoreRef = useRef(onLoadMore);
  const loadingMoreRef = useRef(isLoadingMore);
  loadMoreRef.current = onLoadMore;
  loadingMoreRef.current = isLoadingMore;

  const PaginatingMenuList = useMemo(() => {
    const MenuList = (props: MenuListProps<any, boolean, GroupBase<any>>) => {
      const handleScroll = (e: React.UIEvent<HTMLDivElement>) => {
        const el = e.currentTarget;
        if (
          el.scrollHeight - el.scrollTop - el.clientHeight <
          LOAD_MORE_THRESHOLD_PX
        ) {
          loadMoreRef.current?.();
        }
      };
      return (
        <RSComponents.MenuList
          {...props}
          innerProps={{ ...props.innerProps, onScroll: handleScroll }}
        >
          {props.children}
          {loadingMoreRef.current ? (
            <div
              style={{
                padding: "8px",
                textAlign: "center",
                fontSize: "12px",
                color: "#999",
              }}
            >
              Loading…
            </div>
          ) : null}
        </RSComponents.MenuList>
      );
    };
    return MenuList;
  }, []);

  const selectComponents = onLoadMore
    ? { IndicatorSeparator: () => null, MenuList: PaginatingMenuList }
    : { IndicatorSeparator: () => null };

  return (
    <Form.Group
      className={`react_select_wrap ${rest.className}`}
      controlId={name}
    >

      {label ? <Form.Label style={{ margin: margin ? '5px' : '' }}>{label}</Form.Label> : null
      }

      <Select
        components={selectComponents}
        isSearchable
        isClearable={isClearable}
        options={options}
        // Server handles search when paginating, so don't also filter locally.
        filterOption={onLoadMore ? () => true : undefined}
        // While loading, hide the built-in "No options" so the "Loading…"
        // footer is the only message shown; "No options" only after it resolves.
        noOptionsMessage={() => (isLoadingMore ? null : "No options")}
        classNamePrefix="react_select"
        onChange={(e: any) => {
          if (name === "state") {
            formik?.setFieldValue("state", e ? e.id : null);
          } else {
            formik?.setFieldValue(name, e || null);
          }
        }}
        // onChange={
        //   formik ? (e: any) => formik?.setFieldValue(name, e ?? "") : () => {}
        // }
        {...rest}
        styles={{
          option: (styles, { data, isDisabled, isFocused, isSelected }) => {
            return {
              ...styles,
              backgroundColor: isDisabled ? "" : "none",
              wordBreak: "break-word",
              wordWrap: "break-word",
              color: isSelected
                ? "#2549E8 !important"
                : isDisabled
                  ? "#a9b0bf !important"
                  : "",
              ":active": {
                backgroundColor: "none",
              },
              cursor: isDisabled ? "not-allowed" : "pointer",
            };
          },
          clearIndicator: (styles) => ({
            ...styles,
            color: "#999",
            cursor: "pointer",
            ":hover": {
              color: "#333",
            },
          }),
        }}
        escapeClearsValue={true}
        value={
          name === "state"
            ? options?.filter((item: any) => item?.id === formikValue)
            : formikValue || null
        }
        className={`${formikError ? "has_error" : ""}`}
      />
      {
        formikError && formikErrorMesg ? (
          <span className="error">
            {typeof formikErrorMesg?.value === "string"
              ? formikErrorMesg?.value
              : ""}
          </span>
        ) : (
          <span className="error">
            {typeof formikError === "string" ? formikError : ""}
          </span>
        )
      }
    </Form.Group >
  );
};

export default ReactSelectField;
